import { type } from "rambda"
import { pascalCase } from "string-fn"

import { OFF } from "../config/constants"
import { settingsContextInitialValue } from "./settings"
import { isObject } from "./utils/utils"
import { mergeDeep, precondition } from "./utils/utils"

const COLOR_THEME_KEY = "color-theme"
const APP_SETTINGS_KEY = "app-settings"
const INDEXES_KEY = "indexes"

export const initialAppSettings = {
  selectCorrectWord: {
    book: "",
    hardMode: OFF,
    limitQuestionsMode: "",
    readAsBookMode: OFF,
  },
}

type CreateGetter<T, K extends string> = Record<Getter<K>, ()=> T>
type CreateSetter<T, K extends string> = Record<Setter<K>, (x: T)=> void>

type Getter<T extends string> = `get${PascalCase<T>}`
type PascalCase<Str extends string> = Str extends `${infer Start}-${infer End}`
  ? PascalCase<`${Capitalize<Start>}${Capitalize<End>}`>
  : Capitalize<Str>

type Setter<T extends string> = `set${PascalCase<T>}`

/**
 * Creates getter and setter functions for a given key in localStorage.
 */
function createSetterGetters<T, K extends string>(
  key: K,
  defaultValue: T,
  transformGetter?: (value: string)=> T,
): CreateGetter<T, K> & CreateSetter<T, K> {
  // SAFETY: pascalCase produces valid getter/setter names
  const getterName = `get${pascalCase(key)}` as Getter<K>
  // SAFETY: pascalCase produces valid getter/setter names
  const setterName = `set${pascalCase(key)}` as Setter<K>

  return {
    [getterName](): T {
      const value = localStorage.getItem(key)
      if (value !== null) {
        // SAFETY: localStorage value is string, cast to T
        const resolvedValue = transformGetter
          ? transformGetter(value)
          : (value as T)

        if (type(resolvedValue) === "Object") {
          return mergeDeep(defaultValue, resolvedValue)
        }

        return resolvedValue
      }

      return defaultValue
    },
    [setterName](value: T) {
      let valueToStore: T = value
      if (isObject(value)) {
        // SAFETY: JSON.stringify produces a string, stored as T
        valueToStore = JSON.stringify(value) as T
      }
      // SAFETY: valueToStore is string or stringified object
      localStorage.setItem(key, valueToStore as string)
    },
  // SAFETY: object literal matches CreateGetter & CreateSetter shape
  } as CreateGetter<T, K> & CreateSetter<T, K>
}

export const Storage = {
  ...createSetterGetters(
    APP_SETTINGS_KEY,
    settingsContextInitialValue.appSettings,
    JSON.parse,
  ),
  ...createSetterGetters(
    INDEXES_KEY,
    settingsContextInitialValue.index,
    JSON.parse,
  ),
  ...createSetterGetters(COLOR_THEME_KEY, "light"),
}

export const LocalStorage = {
  get<T>(key: string, transform?: (x: string)=> T) {
    const found = localStorage.getItem(key)
    precondition(found, "localstorage.get")

    // SAFETY: transform produces T, or String fallback is used as T
    return (transform ?? String)(found) as T
  },
  set(key: string, value: number | string) {
    localStorage.setItem(key, String(value))
  },
}
